// One campaign: /growth/campaigns/:id. Its numbers, the verbs its status allows
// and the per-recipient log (?status=&page= in the URL). "Add to schedule" is the
// start, and the owner's approval of every email the campaign plans: its dialog
// says who's in it, each email, the start, the cap and the window. After it the
// cap and window save in place; the audience and emails are fixed. Pause stops it;
// one email comes off on the Schedule.

import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage, isApiError } from '@/api/client'
import { SEND_STATUSES, type Campaign, type CampaignRecipient, type CampaignWrite, type SendStatus } from '@/api/growthCampaignTypes'
import { PageHeader } from '@/components/shell/PageHeader'
import {
  Button,
  ButtonLink,
  Card,
  CardHeader,
  ConfirmDialog,
  DataTable,
  EditableTextField,
  EmptyState,
  ErrorState,
  InlineAlert,
  KeyValueList,
  LoadingState,
  Pagination,
  RelativeTime,
  SelectInput,
  SkeletonRows,
  StatTile,
  TrashIcon,
  useToast,
  type Column,
} from '@/components/ui'
import { formatDateTime } from '@/lib/format'
import { ACTIVITY_LABELS, BUCKET_LABELS, VERIFY_LABELS } from '@/lib/growth'
import { SEND_STATUS_LABELS, sendDetail } from '@/lib/growthCampaigns'
import { useDocumentTitle } from '@/lib/hooks'
import { invalidateQueries, useQuery } from '@/lib/query'
import { CampaignStatusBadge, SendStatusBadge } from './campaignShared'

const PAGE_SIZE = 25

function recipientWhat(r: CampaignRecipient): string {
  const events = [
    r.clicked_at ? 'Clicked' : null,
    r.replied_at ? 'Replied' : null,
    r.bounced_at ? `Bounced (${r.bounce_kind ?? 'hard'})` : null,
    r.unsubscribed_at ? 'Unsubscribed' : null,
  ].filter(Boolean)
  return [sendDetail(r), ...events].filter(Boolean).join(' · ')
}

const RECIPIENT_COLUMNS: Column<CampaignRecipient>[] = [
  {
    id: 'who',
    header: 'Lead',
    primary: true,
    cell: (r) => (
      <span className="grid min-w-0 gap-0.5">
        {r.lead ? (
          <Link to={`/growth/leads/${r.lead.id}`} className="break-words font-semibold text-ink hover:text-accent">
            {r.lead.name}
          </Link>
        ) : (
          <span className="font-semibold text-muted">Deleted lead</span>
        )}
        <span className="font-mono text-xs font-medium text-muted [overflow-wrap:anywhere]">{r.email}</span>
      </span>
    ),
    className: 'min-w-[13rem] max-w-[18rem]',
  },
  { id: 'step', header: 'Email', cell: (r) => <span className="text-muted">{r.step_number === 1 ? 'First' : `Follow-up ${r.step_number - 1}`}</span> },
  { id: 'status', header: 'Status', cell: (r) => <SendStatusBadge status={r.status} /> },
  { id: 'when', header: 'When', cell: (r) => <RelativeTime value={r.sent_at ?? r.scheduled_for} fallback="Not yet" className="text-muted" /> },
  { id: 'what', header: 'What happened', cell: (r) => <span className="text-muted">{recipientWhat(r) || '—'}</span> },
]

function RecipientLog({ campaignId }: { campaignId: number }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const rawStatus = searchParams.get('status')
  const status = (SEND_STATUSES as readonly string[]).includes(rawStatus ?? '') ? (rawStatus as SendStatus) : undefined
  const pageParam = Number(searchParams.get('page'))
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1
  const params = { status, page, page_size: PAGE_SIZE }
  const { data, error, status: loadStatus, refetch } = useQuery(['growth', 'recipients', campaignId, params], (signal) => api.growth.campaigns.recipients(campaignId, params, signal), { keepPrevious: true })

  const update = (patch: Record<string, string | number | undefined>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined || v === '' || (k === 'page' && v === 1)) next.delete(k)
          else next.set(k, String(v))
        }
        if (!('page' in patch)) next.delete('page')
        return next
      },
      { replace: true },
    )

  return (
    <Card flush className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
        <h2 className="text-base font-bold text-ink">Recipients</h2>
        <SelectInput
          aria-label="Filter recipients by status"
          value={status ?? ''}
          options={[{ value: '', label: 'Any status' }, ...SEND_STATUSES.map((s) => ({ value: s, label: SEND_STATUS_LABELS[s] }))]}
          onChange={(e) => update({ status: e.target.value })}
          className="h-8 w-44"
        />
      </div>
      {loadStatus === 'loading' ? (
        <SkeletonRows rows={4} label="Loading recipients" />
      ) : loadStatus === 'error' && !data ? (
        <ErrorState error={error} title="Couldn't load the recipients" onRetry={refetch} />
      ) : !data || data.recipients.length === 0 ? (
        <EmptyState title="Nobody here yet" description={status ? 'Try another status.' : 'People appear here as each day is planned.'} />
      ) : (
        <>
          <DataTable caption="Recipients" rows={data.recipients} columns={RECIPIENT_COLUMNS} rowKey={(r) => r.id} />
          <div className="border-t border-line px-4 py-3">
            <Pagination page={data.page} pageSize={data.page_size} total={data.total} noun="recipients" onPageChange={(p) => update({ page: p })} />
          </div>
        </>
      )}
    </Card>
  )
}

function audienceText(c: Campaign): string {
  const a = c.audience
  const parts = [
    a.statuses?.length ? a.statuses.join(', ') : 'new, researching',
    (a.verify?.length ? a.verify : (['verified'] as const)).map((v) => VERIFY_LABELS[v].toLowerCase()).join(', '),
    `${(a.activity?.length ? a.activity : (['active'] as const)).map((s) => ACTIVITY_LABELS[s].toLowerCase()).join(', ')} activity`,
    a.types?.length ? a.types.join(', ') : null,
    a.states?.length ? a.states.join(', ') : null,
    a.platforms?.length ? a.platforms.join(', ') : null,
    a.platform_ids?.length ? `${a.platform_ids.length} ${a.platform_ids.length === 1 ? 'platform' : 'platforms'}` : null,
    a.lead_ids?.length ? `${a.lead_ids.length} picked ${a.lead_ids.length === 1 ? 'lead' : 'leads'}` : null,
    a.buckets?.length ? a.buckets.map((b) => BUCKET_LABELS[b]).join(', ') : 'every bucket but Skip',
    a.min_priority ? `priority ${a.min_priority}+` : null,
    a.q ? `matching "${a.q}"` : null,
  ]
  return parts.filter(Boolean).join(' · ')
}

/** What "Add to schedule" approves: who's in it, each email, the start, the cap and the window. */
function ScheduleSummary({ campaign: c, includesStale, includesReview }: { campaign: Campaign; includesStale: boolean; includesReview: boolean }) {
  const audience = useQuery(['growth', 'audience-preview', c.id, c.audience], (signal) => api.growth.campaigns.previewAudience(c.audience, signal))
  const who = audience.data
  return (
    <div className="grid gap-3 text-[13px]" data-testid="schedule-summary">
      <KeyValueList
        items={[
          {
            label: "Who's in it",
            value: who ? (
              <span>
                {who.eligible} {who.eligible === 1 ? 'lead' : 'leads'} can be emailed today ({audienceText(c)}).
                {who.sample.length ? ` First: ${who.sample.slice(0, 3).map((l) => l.name).join(', ')}${who.eligible > 3 ? ', …' : ''}.` : ''}
                {' '}Who's in is checked again each day, and at each send.
              </span>
            ) : audience.status === 'error' ? (
              audienceText(c)
            ) : (
              'Counting…'
            ),
          },
          {
            label: 'Emails',
            value: (
              <ol className="grid gap-0.5">
                {c.steps.map((s) => (
                  <li key={s.id}>
                    {s.step_number === 1 ? 'First' : `Then ${s.delay_days} ${s.delay_days === 1 ? 'day' : 'days'} later`}: {s.template_name}
                  </li>
                ))}
              </ol>
            ),
          },
          { label: 'Start', value: c.starts_at ? formatDateTime(c.starts_at) : 'Today' },
          { label: 'Most per day', value: c.daily_cap },
          { label: 'Window', value: `${c.window_start} to ${c.window_end}, ${c.time_zone}` },
        ]}
      />
      {includesStale ? <p className="text-warn">It includes stale leads.</p> : null}
      {includesReview ? <p className="text-warn">It includes leads that need review.</p> : null}
      <p className="text-muted">Pause the campaign to stop it. To stop one email, remove it on the Schedule.</p>
    </div>
  )
}

function LiveSettings({ campaign, save }: { campaign: Campaign; save: (patch: CampaignWrite) => Promise<unknown> }) {
  const time = (v: string) => (/^([01]\d|2[0-3]):[0-5]\d$/.test(v) ? null : 'Use a time like 09:00.')
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <EditableTextField label="Most per day" value={String(campaign.daily_cap)} maxLength={4} validate={(v) => (/^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= 2000 ? null : 'Use a whole number from 1 to 2,000.')} onSave={(v) => save({ daily_cap: Number(v) })} />
      <EditableTextField label="Window opens" value={campaign.window_start} maxLength={5} validate={time} onSave={(window_start) => save({ window_start })} />
      <EditableTextField label="Window closes" value={campaign.window_end} maxLength={5} validate={time} onSave={(window_end) => save({ window_end })} />
    </div>
  )
}

export default function CampaignDetailPage() {
  const { id: rawId } = useParams()
  const id = Number(rawId)
  const validId = Number.isInteger(id) && id > 0
  const navigate = useNavigate()
  const toast = useToast()
  const [dialog, setDialog] = useState<'delete' | 'start' | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const { data: c, error, status, refetch } = useQuery(validId ? ['growth', 'campaign', id] : null, (signal) => api.growth.campaigns.get(id, signal))
  useDocumentTitle(c?.name ?? 'Campaign')

  if (!validId || (status === 'error' && isApiError(error) && error.code === 'not_found')) {
    return (
      <Card>
        <EmptyState title="Campaign not found" description="It may have been deleted." action={<ButtonLink to="/growth/campaigns">Back to campaigns</ButtonLink>} />
      </Card>
    )
  }
  if (status === 'loading' && !c) return <LoadingState label="Loading campaign…" />
  if (!c) {
    return (
      <Card>
        <ErrorState error={error} title="Couldn't load this campaign" onRetry={refetch} />
      </Card>
    )
  }

  const act = async (fn: () => Promise<unknown>) => {
    setProblem(null)
    try {
      await fn()
      invalidateQueries('growth')
    } catch (err) {
      setProblem(errorMessage(err))
    }
  }
  const save = async (patch: CampaignWrite) => {
    await api.growth.campaigns.update(c.id, patch)
    invalidateQueries('growth')
  }

  const problems = c.problems ?? []
  const draft = c.status === 'draft'
  const live = c.status === 'scheduled' || c.status === 'running'
  const includesStale = !!c.audience.activity?.includes('stale')
  const includesReview = !!c.audience.verify?.includes('needs_review')

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        crumbs={[{ label: 'Campaigns', to: '/growth/campaigns' }, { label: c.name }]}
        title={c.name}
        badges={<CampaignStatusBadge status={c.status} />}
        actions={
          <>
            {draft ? (
              <>
                <Button icon={<TrashIcon size={15} />} onClick={() => setDialog('delete')}>
                  Delete…
                </Button>
                <ButtonLink to={`/growth/campaigns/${c.id}/edit`}>Edit</ButtonLink>
                <Button variant="primary" disabled={problems.length > 0} onClick={() => setDialog('start')}>
                  Add to schedule…
                </Button>
              </>
            ) : null}
            {live ? <Button onClick={() => void act(() => api.growth.campaigns.pause(c.id))}>Pause</Button> : null}
            {c.status === 'paused' ? (
              <Button variant="primary" onClick={() => void act(() => api.growth.campaigns.resume(c.id))}>
                Resume
              </Button>
            ) : null}
          </>
        }
      />

      {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
      {c.paused_reason ? (
        <InlineAlert tone="warn" title="Paused">
          {c.paused_reason}
        </InlineAlert>
      ) : null}
      {includesStale && c.status !== 'done' ? (
        <InlineAlert tone="warn" title="This campaign includes stale leads">
          The newest evidence of a season for those leagues is old, so they may be finished. They're here because someone ticked them.
        </InlineAlert>
      ) : null}
      {includesReview && c.status !== 'done' ? (
        <InlineAlert tone="warn" title="This campaign includes leads that need review">
          Something about those leagues wants a person's eye first. They're here because someone ticked them.
        </InlineAlert>
      ) : null}
      {problems.length > 0 ? (
        <InlineAlert tone="warn" title="This campaign can't start yet">
          <ul className="mt-1 grid gap-1">
            {problems.map((p) => (
              <li key={p.message}>{p.message}</li>
            ))}
          </ul>
        </InlineAlert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatTile label="Sent" value={c.stats.sent} hint={c.stats.dry_run ? `${c.stats.dry_run} dry run` : undefined} />
        <StatTile label="Clicked" value={c.stats.clicked} />
        <StatTile label="Replied" value={c.stats.replied} />
        <StatTile label="Bounced" value={c.stats.bounced} tone={c.stats.bounced ? 'warn' : 'default'} />
        <StatTile label="Unsubscribed" value={c.stats.unsubscribed} />
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-[1fr_1fr]">
        <Card>
          <CardHeader title="Settings" />
          {c.status === 'done' || draft ? null : <LiveSettings campaign={c} save={save} />}
          <KeyValueList
            className={c.status === 'done' || draft ? undefined : 'mt-4 border-t border-line pt-4'}
            items={[
              { label: 'Who', value: audienceText(c) },
              { label: 'Start', value: c.starts_at ? formatDateTime(c.starts_at) : 'When started' },
              { label: 'Window', value: `${c.window_start} to ${c.window_end}, ${c.time_zone}` },
              {
                label: 'Approval',
                value: draft
                  ? 'Adding it to the schedule approves every email it plans, follow-ups too'
                  : c.started_by
                    ? `Every email it plans, given by ${c.started_by.name ?? c.started_by.email} when they added it to the schedule${c.started_at ? ` (${formatDateTime(c.started_at)})` : ''}`
                    : 'Every email it plans, given when it was added to the schedule',
              },
              ...(c.status === 'done' || draft ? [{ label: 'Most per day', value: c.daily_cap }] : []),
            ]}
          />
        </Card>
        <Card>
          <CardHeader title="Emails" />
          {c.steps.length === 0 ? (
            <p className="text-[13px] text-muted">None yet.</p>
          ) : (
            <ol className="grid gap-2">
              {c.steps.map((s) => (
                <li key={s.id} className="text-[13.5px]">
                  <Link to={`/growth/templates/${s.template_id}`} className="font-semibold text-ink underline decoration-line-strong underline-offset-2 hover:text-accent">
                    {s.template_name}
                  </Link>
                  <span className="text-muted"> · {s.step_number === 1 ? 'first' : `${s.delay_days} ${s.delay_days === 1 ? 'day' : 'days'} after`}</span>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>

      {draft ? null : <RecipientLog campaignId={c.id} />}

      <ConfirmDialog
        open={dialog === 'start'}
        onClose={() => setDialog(null)}
        title={`Add ${c.name} to the schedule?`}
        description="This is your approval of every email it plans, follow-ups too. Nothing waits for you each day: check what you're approving."
        confirmLabel="Add to schedule"
        tone="primary"
        onConfirm={async () => {
          await api.growth.campaigns.start(c.id)
          invalidateQueries('growth')
          toast.success(`${c.name} is on the schedule.`)
        }}
      >
        {dialog === 'start' ? <ScheduleSummary campaign={c} includesStale={includesStale} includesReview={includesReview} /> : null}
      </ConfirmDialog>
      <ConfirmDialog
        open={dialog === 'delete'}
        onClose={() => setDialog(null)}
        title={`Delete ${c.name}?`}
        description="This removes the draft. Its templates stay."
        confirmLabel="Delete campaign"
        onConfirm={async () => {
          await api.growth.campaigns.delete(c.id)
          invalidateQueries('growth')
          navigate('/growth/campaigns')
        }}
      />
    </div>
  )
}
